import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import {
  useAppDispatch,
  useAppSelector,
  useScrollRestoration,
  useUserTimezone,
} from '@/shared/hooks';
import { groupByDay, safeSessionStorage, trackEvent } from '@/shared/lib';

import {
  composedModel,
  useTranscriptorStartAvailability,
} from '@/entities/composed';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import {
  getConsultationPath,
  sortConsultationsByDate,
  type TranscriptorConsultation,
  useRemoveConsultation,
  useReorderConsultation,
  useStaleConsultationDetection,
  useTranscriptorConsultations,
} from '@/entities/transcriptor';

import {
  useHideConsultation,
  useRestoreConsultation,
} from '@/features/hideConsultation';

import { RECORDINGS_SCROLL_KEY_PREFIX } from '../config';

const getConsultationDate = (consultation: TranscriptorConsultation) =>
  consultation.date;

export const usePatientRecordingsPage = (patientID: string) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userTimezone = useUserTimezone();

  const { CanRequestReport } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID),
  );
  const enableArchiveReportsFeature = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_ArchiveReports,
    ),
  );

  const canViewArchive = useAppSelector(
    composedModel.selectors.selectCanViewPatientRecordingsArchive(patientID),
  );

  const { consultations, hiddenConsultations, visibleTotal, isLoaded, error } =
    useTranscriptorConsultations(patientID, {
      includeHidden: canViewArchive,
    });
  const { isConsultationStale, forceStop } = useStaleConsultationDetection();

  const removeConsultation = useRemoveConsultation();
  const hideConsultation = useHideConsultation();
  const restoreConsultation = useRestoreConsultation();
  const reorderConsultation = useReorderConsultation();

  const {
    funding,
    canStartRecording,
    isStartRecordingDisabled,
    isStorageBlocked,
  } = useTranscriptorStartAvailability();

  const groups = groupByDay(
    sortConsultationsByDate(consultations),
    getConsultationDate,
  );
  const hiddenGroups = groupByDay(
    sortConsultationsByDate(hiddenConsultations),
    getConsultationDate,
  );

  const scrollKey = `${RECORDINGS_SCROLL_KEY_PREFIX}${patientID}`;

  useScrollRestoration({
    storageKey: scrollKey,
    isContentLoaded: isLoaded,
    maxAttempts: 10,
    attemptInterval: 200,
    timeoutMinutes: 5,
  });

  useEffect(() => {
    trackEvent({
      eventName: 'PatientRecordingsPageViewed',
      properties: { patient_id: patientID },
      isPageViewEvent: true,
    });
  }, [patientID]);

  const handleOpenRecording = (consultation: TranscriptorConsultation) => {
    safeSessionStorage.setItem(scrollKey, String(window.scrollY));
    safeSessionStorage.setItem('lastScrollTime', String(Date.now()));

    trackEvent({
      eventName: 'PatientRecordingsPageRecordingClicked',
      properties: { patient_id: patientID },
    });

    navigate(
      getConsultationPath({ patientID, consultationID: consultation.id }),
    );
  };

  const handleStartRecording = () => {
    trackEvent({
      eventName: 'PatientRecordingsPageStartRecordingButtonClicked',
      properties: { patient_id: patientID },
    });

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.TranscriptorRecording,
        data: { patientID },
      }),
    );
  };

  return {
    groups,
    hiddenGroups,
    hiddenCount: hiddenConsultations.length,
    visibleTotal,
    isLoaded,
    hasLoadError: !isLoaded && Boolean(error),
    isEmpty: consultations.length === 0 && hiddenConsultations.length === 0,
    showHiddenSection: canViewArchive && hiddenConsultations.length > 0,
    hideMode: enableArchiveReportsFeature,
    userTimezone,
    funding,
    canStartRecording: canStartRecording && CanRequestReport,
    isStartRecordingDisabled,
    isStorageBlocked,
    isConsultationStale,
    rowActions: {
      onOpen: handleOpenRecording,
      onForceStop: forceStop,
      onArchive: hideConsultation,
      onDelete: removeConsultation,
      onReorder: reorderConsultation,
      onRestore: restoreConsultation,
    },
    handleStartRecording,
  };
};
